/**
 * SB6-II-01 — inline Assigned Company cell.
 *
 * Single-select. Interactive **only** when `row.assignedCompanyEditable`
 * is true (the parent project is owned by the current tenant — OQ-II-8);
 * otherwise renders the company name (or `"-"`) as plain text. When
 * interactive, clicking opens a `<Popover>` with a single-select
 * `<Autocomplete>` over `options/companies?q=` (lazy-loaded). Commit
 * fires `PATCH {assignedCompanyTenantId}` with optimistic update +
 * rollback + toast.
 *
 * Company options (TNCY-5 scope d): the `options/companies?q=` source is
 * now backed by the real cross-tenant known-tenants set (TNCY-2 repointed
 * it). It lists the caller's own company ∪ actively-linked partner tenants
 * (suspended links are excluded server-side). No FE change here beyond the
 * picker now surfacing partners — see Administration → Partner Companies to
 * manage the links that populate this set.
 */
import { useState, type ReactElement } from "react";
import {
  Autocomplete,
  Box,
  CircularProgress,
  Popover,
  Stack,
  TextField,
  Typography,
  type TextFieldProps,
} from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useToast } from "../account/ToastContext.js";
import { iiKeys } from "./keys.js";
import { useIiCompanyOptionsQuery } from "./useInspectionInstructionFacets.js";
import { usePatchInspectionInstructionMutation } from "./mutations.js";
import { applyOptimisticRowPatch } from "./optimisticListUpdate.js";
import type { IiCompanyOption, IiListItem, IiListResponse } from "./types.js";

export interface AssignedCompanyCellProps {
  row: IiListItem;
}

export function AssignedCompanyCell({
  row,
}: AssignedCompanyCellProps): ReactElement {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [search, setSearch] = useState("");
  const open = anchorEl !== null;

  const companies = useIiCompanyOptionsQuery(search, open);
  const patch = usePatchInspectionInstructionMutation();

  const label = row.assignedCompanyName ?? "-";

  // Non-editable rows: plain text (read-only) — AC11.
  if (!row.assignedCompanyEditable) {
    return (
      <Typography variant="body2" component="span">
        {label}
      </Typography>
    );
  }

  function handleOpen(e: React.MouseEvent<HTMLElement>): void {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
  }
  function handleClose(): void {
    setAnchorEl(null);
    setSearch("");
  }

  function commit(next: IiCompanyOption | null): void {
    const family = iiKeys.lists();
    const snapshots = qc.getQueriesData<IiListResponse>({ queryKey: family });
    for (const [key, data] of applyOptimisticRowPatch(
      snapshots,
      row.inspectionInstructionId,
      {
        assignedCompanyTenantId: next?.id ?? null,
        assignedCompanyName: next?.label ?? null,
      },
    )) {
      if (data) qc.setQueryData(key, data);
    }
    patch.mutate(
      {
        id: row.inspectionInstructionId,
        body: { assignedCompanyTenantId: next?.id ?? null },
      },
      {
        onError: () => {
          for (const [key, data] of snapshots) {
            if (data) qc.setQueryData(key, data);
          }
          toast("Couldn't update company — try again.", "error");
        },
        onSuccess: () => {
          toast("Assigned company updated.");
        },
      },
    );
    handleClose();
  }

  // Resolve the current value to an option shape for the controlled
  // Autocomplete (label still renders even if not in `options` yet).
  const currentOption: IiCompanyOption | null =
    row.assignedCompanyTenantId !== null
      ? {
          id: row.assignedCompanyTenantId,
          label: row.assignedCompanyName ?? row.assignedCompanyTenantId,
        }
      : null;
  const optionList: IiCompanyOption[] = (() => {
    const out = [...(companies.data?.items ?? [])];
    if (currentOption && !out.some((o) => o.id === currentOption.id)) {
      out.unshift(currentOption);
    }
    return out;
  })();

  return (
    <>
      <Box
        onClick={handleOpen}
        role="button"
        tabIndex={0}
        aria-haspopup="dialog"
        aria-label={`Edit assigned company (${label})`}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setAnchorEl(e.currentTarget);
          }
        }}
        sx={{ width: "100%", cursor: "pointer", minHeight: 24 }}
      >
        <Typography
          variant="body2"
          component="span"
          color={row.assignedCompanyName ? "text.primary" : "text.secondary"}
        >
          {label}
        </Typography>
      </Box>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        slotProps={{ paper: { sx: { width: 320, p: 1.5 } } }}
      >
        <Stack spacing={1}>
          <Typography variant="caption" color="text.secondary">
            Assigned company
          </Typography>
          <Autocomplete<IiCompanyOption>
            openOnFocus
            size="small"
            options={optionList}
            value={currentOption}
            loading={companies.isPending}
            inputValue={search}
            onInputChange={(_e, value, reason) => {
              if (reason === "reset") return;
              setSearch(value);
            }}
            getOptionLabel={(o) => o.label}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            onChange={(_e, next) => {
              commit(next);
            }}
            renderInput={(p) => {
              const { InputLabelProps, InputProps, inputProps, ...rest } = p;
              const tfProps = {
                ...rest,
                placeholder: "Search companies…",
                slotProps: {
                  htmlInput: {
                    ...inputProps,
                    "aria-label": "Search companies",
                  },
                  input: {
                    ...InputProps,
                    endAdornment: (
                      <>
                        {companies.isPending ? (
                          <CircularProgress size={14} />
                        ) : null}
                        {InputProps.endAdornment}
                      </>
                    ),
                  },
                  inputLabel: InputLabelProps,
                },
              } as unknown as TextFieldProps;
              return <TextField {...tfProps} />;
            }}
          />
        </Stack>
      </Popover>
    </>
  );
}
